import * as React from "react";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import clsx from "clsx";
import { Typography, CardContent, Divider } from "@mui/material";

const PREFIX = "SearchResultCard";

const classes = {
  card: `${PREFIX}-card`,
  cardTitleHeading: `${PREFIX}-cardTitleHeading`,
  iconAlign: `${PREFIX}-iconAlign`,
  listContent: `${PREFIX}-listContent`,
  listCondensedContent: `${PREFIX}-listCondensedContent`,
  listLabel: `${PREFIX}-listLabel`,
  listValue: `${PREFIX}-listValue`,
  columnContainer: `${PREFIX}-columnContainer`,
};

const StyledCard = styled(Card)(() => ({
  [`&.${classes.card}`]: {
    "marginTop": 24,
    "marginBottom": 12,
    "borderRadius": 6,
    "border": "1px solid #acc8df",
    "boxShadow": "0px 0px 10px #D9E8F6",
    "& .MuiTypography-body1": {
      lineHeight: "1rem",
    },
    "&:focus-visible": {
      boxShadow:
        "0px 5px 5px -3px rgba(172,200,223,1), 0px 8px 10px 1px rgba(172,200,223,1), 0px 3px 14px 2px rgba(172,200,223,1)",
    },
  },

  [`& .${classes.cardTitleHeading}`]: {
    "display": "flex",
    "alignItems": "center",
    "width": "100%",
    "margin": 0,
    "paddingBottom": 16,
    "& .cardTitle": {
      padding: 0,
    },
    "& .MuiGrid-item": {
      padding: 0,
    },
  },

  [`& .${classes.iconAlign}`]: {
    textAlign: "right",
  },

  [`& .${classes.listContent}`]: {
    justifyContent: "center",
    display: "flex",
    flexDirection: "row",
    paddingBottom: 0,
    paddingTop: 1,
  },

  [`& .${classes.listCondensedContent}`]: {
    paddingTop: 0,
    padding: 0,
  },

  [`& .${classes.listLabel}`]: {
    textAlign: "left",
    width: "100%",
    height: "100%",
    fontWeight: "bolder",
  },

  [`& .${classes.listValue}`]: {
    width: "100%",
    color: "#666666",
  },

  [`& .${classes.columnContainer}`]: {
    marginTop: 8,
  },
}));

interface Props {
  title: string;
  id: string;
  children: React.ReactNode;
}

interface CardList {
  itemValue: string | number;
  className?: string;
  itemLabel?: string;
  itemValueId?: string;
  sx?: any;
}

export function CardListCondensedItem(props: CardList) {
  return (
    <>
      {props.itemLabel ? (
        <Typography
          component="div"
          aria-label={`${props.itemLabel}.`}
          id={`item-label-${props.itemLabel} value`.replace(/[^A-Z0-9]/gi, "_")}
          sx={(theme) => ({
            display: "block",
            color: theme.palette.primary.main,
            fontWeight: "bold",
            ...props.sx,
          })}
        >
          {props.itemLabel}
        </Typography>
      ) : (
        ""
      )}
      <Typography
        component="div"
        className={classes.listValue}
        aria-label={`${props.itemValue}.`}
        id={`item-value-${
          props.itemValueId ? props.itemValueId : props.itemLabel
        } value`.replace(/[^A-Z0-9]/gi, "_")}
        sx={{
          display: "block",
          ...props.sx,
        }}
      >
        {props.itemValue}
      </Typography>
    </>
  );
}

export default function SearchResultCard({ children, title, id }: Props) {
  return (
    <StyledCard
      id={id}
      raised
      tabIndex={0}
      key={`card-${id}`}
      className={clsx(classes.card, "searchResultCard")}
    >
      <CardContent>
        <Typography
          variant="h6"
          component="span"
          aria-label={`${title}.`}
          color="primary.main"
          className={clsx("instName", classes.cardTitleHeading)}
          sx={{
            display: "inline",
          }}
        >
          {title}
        </Typography>
        <Typography component="div">
          <Divider />
        </Typography>
        {children}
      </CardContent>
    </StyledCard>
  );
}
